import { yupResolver } from "@hookform/resolvers/yup";
import convert from "xml-js";
import axios from "axios";
import { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/generic";
import { TextInputWithRegister } from "src/components/inputs";
import { auth, dynamo, format, informativ } from "src/utils";
import { userTypes } from "src/utils/types";
import { capitalizeFirstLetter } from "src/utils/format/strings";

interface ISubscriberRegistrationFormProps {
  customerId: string;
  currentSelectedTier: string;
}

const SubscriberRegistrationForm: FunctionComponent<
  ISubscriberRegistrationFormProps
> = ({ customerId, currentSelectedTier }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
      password: "",
      reasonForSubscription: "",
      referredBy: "",
    },
    // resolver: yupResolver(forms.schema.ReferralPartnerRegistrationSchema),
  });

  const navigate = useNavigate();

  const save = async (data: any) => {
    const { email, password, name, phone } = data;
    try {
      const result = await auth.cognito.registerCognitoUser(email, password);
      const uid =
        result?.userSub ||
        `${Math.random().toFixed(4)}-${Math.random().toFixed(4)}`.replaceAll(
          ".",
          "e"
        );

      // register informative
      const ipResult = await axios.get("https://api.ipify.org?format=json");
      const informativResult = await informativ.creditUsers.addCreditUser({
        uid,
        name,
        email,
        password,
        ip: ipResult.data.ip,
      });
      const convertedResult = convert.xml2js(informativResult, {
        compact: true,
      });

      const userData: userTypes.SubscriberUserInfo = {
        name: data.name,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        phone: data.phone,
        email: data.email,
        reasonForSubscription: data.reasonForSubscription,
        referredBy: data.referredBy,
      };

      const subscriber = format.users.userDataToSubscriber(
        uid,
        userData,
        customerId,
        //@ts-ignore
        convertedResult["XML_INTERFACE"]["CUS_ID"]["_text"]
      );

      console.log({ subscriber, userData });
      await dynamo.mutations.putUser(subscriber);

      navigate(`/auth/confirm-account?email=${subscriber.userInfo.email}`);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <form
      className="
        max-w-5xl mx-auto bg-white shadow-lg rounded-xl p-8 
        border border-gray-200 
      "
    >
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">
        {capitalizeFirstLetter(currentSelectedTier)} Subscriber Registration
      </h2>

      {/* GRID LAYOUT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextInputWithRegister
          id="name"
          register={register("name")}
          label="Name"
          placeholder="Enter full name..."
          error={errors.name}
        />

        <TextInputWithRegister
          id="email"
          register={register("email")}
          label="Email"
          placeholder="Email..."
          error={errors.email}
        />

        <TextInputWithRegister
          id="password"
          register={register("password")}
          label="Set Password"
          placeholder="Password..."
          error={errors.password}
          password
          showPasswordToggle
        />

        <TextInputWithRegister
          id="phone"
          register={register("phone", {
            onChange: (e) => {
              let value = e.target.value.replace(/\D/g, ""); // keep only numbers
              if (value.length > 10) value = value.slice(0, 10);

              if (value.length > 6) {
                value = `(${value.slice(0, 3)})${value.slice(
                  3,
                  6
                )}-${value.slice(6)}`;
              } else if (value.length > 3) {
                value = `(${value.slice(0, 3)})${value.slice(3)}`;
              } else if (value.length > 0) {
                value = `(${value}`;
              }

              e.target.value = value;
            },
          })}
          label="Phone Number"
          placeholder="Phone..."
          error={errors.phone}
        />

        <TextInputWithRegister
          id="address"
          register={register("address")}
          label="Address"
          placeholder="Enter Address..."
          error={errors.address}
        />

        <TextInputWithRegister
          id="city"
          register={register("city")}
          label="City"
          placeholder="Enter City..."
          error={errors.city}
        />

        <TextInputWithRegister
          id="state"
          register={register("state", {
            onChange: (e) => {
              let value = e.target.value;
              if (value.length > 2) value = value.slice(0, 2);

              e.target.value = value;
            },
          })}
          label="State"
          placeholder="State..."
          error={errors.state}
        />

        <TextInputWithRegister
          id="zip"
          register={register("zip", {
            onChange: (e) => {
              let value = e.target.value;
              if (value.length > 5) value = value.slice(0, 5);

              e.target.value = value;
            },
          })}
          label="Zip Code"
          placeholder="Zip..."
          error={errors.zip}
        />

        <TextInputWithRegister
          id="reasonForSubscription"
          register={register("reasonForSubscription")}
          label="Reason for Choosing ClientVitals"
          placeholder="..."
          error={errors.reasonForSubscription}
        />

        <TextInputWithRegister
          id="referredBy"
          register={register("referredBy")}
          label="Referred By (optional)"
          placeholder="Referrer name..."
          error={errors.referredBy}
        />
      </div>

      <div className="mt-10 flex items-center justify-end">
        <a
          className="text-primary hover:underline"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.dropbox.com/scl/fi/ueb58s7eli31z6jfry3ke/ToS.pdf?rlkey=nq3erxn19cj0x808l7fapfb7t&st=aaexcp2s&dl=0"
        >
          <p className="mr-4">
            By Creating An Account You Agree to ClientVitals' Terms and
            Conditions
          </p>
        </a>
        <Button text="Create Account" onClick={handleSubmit(save)} />
      </div>
    </form>
  );
};

export default SubscriberRegistrationForm;
